import { Serializable } from "@/core/generated/api/decorators/serializable_decorator"
import { TaskStatus } from "../constants/taskStatus"
import {
  IsNotEmpty,
  IsOptional as IsOptionalValidator,
  MaxLength,
  ValidateIf,
} from "class-validator"
import { v4 } from "uuid"
import {
  IsDateAfter,
  IsOnlyOneFilled,
  IsRelatedDateAfter,
} from "../core/utils/validators/IsOnlyOneFIlled"
import { ValidateIfIntermediate } from "../core/utils/validators/ValidateIfIntermediate"
import { SkipIfNotIsShared } from "../core/utils/validators/SkipIfNotIsShared"
import { TaskKanbanStatus } from "../core/constants/taskKanbanStatus"
import { IsBeforeRelatedDate } from "../core/utils/validators/isBeforeRelatedDate"
import { IsValidDate } from "../core/utils/validators/IsValidDate"
import { ServiceType } from "../active/constants/serviceType"
import { EngagementType } from "../active/constants/engagementType"
import { RFSCustomType } from "../active/constants/rfsCustomType"
import { SubAreaModel } from "@/modules/prices/models/subAreaModel"
import { RFSStatus } from "../active/constants/rfsStatus"
import { ApprovalStatus } from "@/modules/approval/constants/approvalStatus"

@Serializable
export class RFQTaskModel {
  @IsNotEmpty({
    message: "about_customer.agreement_error",
    groups: ["about_customer"],
  })
  uid: string = v4()

  @IsNotEmpty({
    message: "details.case_title_error",
    groups: [
      "details",
      "request_details",
      "summary",
      "rfs_details",
      "seven_n_details",
    ],
  })
  @IsNotEmpty({
    message: "common:must_be_filled",
    groups: ["edit_title", "edit_dsv_title"],
  })
  name: string = ""

  @IsOptionalValidator({ groups: ["details", "seven_n_details"] })
  caseNumber: string = ""

  @IsOptionalValidator({ groups: ["details", "seven_n_details"] })
  subtitle: string = ""

  @IsOptionalValidator({ groups: ["description"] })
  description: string = ""

  @IsOptionalValidator({ groups: ["prices"] })
  state: TaskStatus = TaskStatus.Drafted

  @IsOptionalValidator({
    groups: ["dates", "edit_dates", "edit_dates_no_intermediate"],
  })
  @IsNotEmpty({
    message: "common:must_be_filled",
    groups: ["rfs_dates", "edit_rfs_dates"],
  })
  @IsValidDate({
    message: "common:invalid_date",
    groups: [
      "dates",
      "edit_dates",
      "edit_dates_no_intermediate",
      "rfs_dates",
      "edit_rfs_dates",
    ],
  })
  @IsDateAfter(new Date(), {
    message: "dates.internal_deadline_after_today",
    groups: ["dates", "rfs_dates"],
  })
  @IsBeforeRelatedDate("customerDeadline", {
    message: "create_proposal:dates.internal_deadline_before_customer_deadline",
    groups: ["dates", "edit_dates", "edit_dates_no_intermediate"],
  })
  @IsBeforeRelatedDate("startDate", {
    message: "create_proposal:dates.deadline_before_start_date",
    groups: ["rfs_dates"],
  })
  internalDeadline: string = ""

  @IsOptionalValidator({ groups: ["dates", "rfs_dates"] })
  @IsValidDate({
    message: "common:invalid_date",
    groups: ["dates", "edit_dates", "edit_dates_no_intermediate"],
  })
  @IsNotEmpty({
    message: "common:must_be_filled",
    groups: ["dates", "edit_dates", "edit_dates_no_intermediate"],
  })
  @IsDateAfter(new Date(), {
    message: "create_proposal:dates.internal_deadline_after_today",
    groups: ["dates"],
  })
  @IsBeforeRelatedDate("startDate", {
    message: "create_proposal:dates.customer_deadline_before_start_date",
    groups: ["dates", "edit_dates", "edit_dates_no_intermediate"],
  })
  customerDeadline: string = ""

  @SkipIfNotIsShared({
    message: "details.workplace_candidates_error",
    groups: ["details", "rfs_details", "seven_n_details"],
  })
  maxNumberCandidates: number = null

  @SkipIfNotIsShared({
    message: "details.workplace_workplace_error",
    groups: ["details", "rfs_details"],
  })
  @MaxLength(44, {
    message: "details.workplace_workplace_error",
    groups: ["details", "rfs_details"],
  })
  workplace: string = ""

  @IsRelatedDateAfter("internalDeadline", {
    message: "dates.sourcing_deadline_before_start_date",
    groups: [],
  })
  @IsValidDate({
    message: "common:invalid_date",
    groups: [
      "details",
      "rfs_details",
      "edit_rfs_dates",
      "duration",
      "seven_n_details",
    ],
  })
  @IsNotEmpty({
    message: "error_fill",
    groups: [
      "details",
      "rfs_details",
      "edit_rfs_dates",
      "duration",
      "seven_n_details",
    ],
  })
  startDate: string = ""

  @IsValidDate({
    message: "common:invalid_date",
    groups: [
      "details",
      "rfs_details",
      "edit_details",
      "edit_rfs_dates",
      "duration",
      "seven_n_details",
    ],
  })
  @IsNotEmpty({
    message: "error_fill",
    groups: [
      "details",
      "rfs_details",
      "edit_rfs_dates",
      "duration",
      "seven_n_details",
    ],
  })
  @IsRelatedDateAfter("startDate", {
    message: "details.end_date_after_start_date",
    groups: [
      "details",
      "rfs_details",
      "edit_details",
      "edit_rfs_dates",
      "duration",
      "seven_n_details",
    ],
  })
  @IsDateAfter(new Date(), {
    message: "details.end_date_after_today",
    groups: ["details", "rfs_details", "edit_details", "seven_n_details"],
  })
  endDate: string = ""

  @SkipIfNotIsShared({
    message: "sharing.error",
    groups: ["details"],
  })
  isContinued: boolean = null

  @SkipIfNotIsShared({ message: "sharing.error", groups: ["dates"] })
  intermediateSale: boolean | null = null
  @ValidateIf((o: RFQTaskModel) => o.intermediateSale === false, {
    groups: ["edit_details", "dates", "edit_dates"],
  })
  @IsValidDate({
    message: "common:invalid_date",
    groups: ["dates", "edit_details", "edit_dates"],
  })
  @IsNotEmpty({
    message: "common:must_be_filled",
    groups: ["dates", "edit_details", "edit_dates"],
  })
  @IsDateAfter(new Date(), {
    message: "dates.deadline_after_today",
    groups: ["dates", "edit_details", "edit_dates"],
  })
  acceptancePeriod: string = ""

  @IsNotEmpty({
    message: "about_customer.customer_error",
    groups: ["about_customer", "customer", "customer_without_task"],
  })
  customerId: string = ""
  customerName: string = ""
  stepIndex: number = 0
  numHoursWeek: number = 37

  @IsOptionalValidator({ groups: ["details", "rfs_details"] })
  extensionMonths: number = 0
  version: number = 0

  @IsNotEmpty({
    message: "sharing.error",
    groups: ["sharing"],
  })
  isShared: boolean = null

  kanbanStatus: TaskKanbanStatus = TaskKanbanStatus.NoCandidates
  assignmentId: string = ""

  // RFS specific fields
  @IsOptionalValidator()
  approvalStatus: ApprovalStatus = ApprovalStatus.Waiting
  serviceType: ServiceType = ServiceType.Consultant
  engagementType: EngagementType = EngagementType.TimeAndMaterial
  projectName: string = ""

  @IsNotEmpty({
    message: "common:must_be_filled",
    groups: ["edit_justification_ordering"],
  })
  justification: string = ""
  requestId: string = ""
  costCenterId: string = ""
  costCenterName: string = ""
  requestStatus: number = RFSStatus.NotDefined
  customType: RFSCustomType = RFSCustomType.Standard

  constructor(init?: Partial<RFQTaskModel>) {
    Object.assign(this, init)
  }
}
